<template>
  <div
    class="the-aside-left"
    :class="{ 'the-aside-left--collapsed': isCollapsed }"
  >
    <nav class="the-aside-left__nav">
      <div
        v-for="(value, name) in visibleMainMenuItems"
        :key="name"
        class="the-aside-left__nav-menu"
      >
        <h2 class="the-aside-left__nav-menu-title">
          {{ name }}
        </h2>
        <template v-for="(library, index) in value">
          <router-link
            :key="`${name}-library-${index}`"
            :to="{ path: library.link }"
            class="the-aside-left__nav-menu-item"
            :title="isCollapsed ? library.title : null"
          >
            <i
              :class="[
                'the-aside-left__nav-menu-item-icon',
                {
                  'the-aside-left__nav-menu-item-icon--faq':
                    library.link === '/faq'
                }
              ]"
              :style="{
                webkitMask: `url('${library.icon}') no-repeat center`,
                mask: `url('${library.icon}') no-repeat center`,
                maskSize: 'contain',
                webkitMaskSize: 'contain'
              }"
            />
            <span class="the-aside-left__nav-menu-item-label">
              {{ library.title }}
            </span>
          </router-link>
        </template>
      </div>
    </nav>

    <div class="the-aside-left__bottom">
      <div class="the-aside-left__bottom-menu">
        <router-link
          v-for="(library, index) in visibleSettingsItems"
          :key="`settings-library-${index}`"
          :to="{ path: library.link }"
          class="the-aside-left__nav-menu-item the-aside-left__nav-menu-item--settings"
          :title="isCollapsed ? library.title : null"
        >
          <i
            :class="[
              'the-aside-left__nav-menu-item-icon',
              {
                'the-aside-left__nav-menu-item-icon--faq':
                  library.link === '/faq'
              }
            ]"
            :style="{
              webkitMask: `url('${library.icon}') no-repeat center`,
              mask: `url('${library.icon}') no-repeat center`,
              maskSize: 'contain',
              webkitMaskSize: 'contain'
            }"
          />
          <span class="the-aside-left__nav-menu-item-label">
            {{ library.title }}
          </span>
        </router-link>
      </div>

      <div class="the-aside-left__footer">
        <button
          class="the-aside-left__toggle"
          type="button"
          :title="`${isCollapsed ? 'Expand' : 'Collapse'} Menu`"
          :aria-expanded="String(!isCollapsed)"
          @click="toggleCollapse"
        >
          <i
            class="the-aside-left__toggle-icon"
            :class="
              isCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'
            "
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FeatureFlags from '@/services/featureFlags'
import { canUserAccessUserManagement } from '@/services/permissions'
import { mapGetters } from 'vuex'

export default {
  name: 'TheAsideLeft',

  data () {
    return {
      isCollapsed: true
    }
  },

  computed: {
    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {Array}
     */
    menuItems () {
      return [
        {
          name: 'Libraries',
          show: () => true,
          items: [
            {
              title: 'Blocks',
              link: '/library/blocks',
              icon: require('@/assets/icons/block.svg')
            },
            {
              title: 'Trims',
              link: '/library/trims',
              icon: require('@/assets/icons/trim.svg')
            },
            {
              title: 'Fabrics',
              link: '/library/fabrics',
              icon: require('@/assets/icons/fabric.svg')
            },
            {
              title: 'Stitches',
              link: '/library/stitches',
              icon: require('@/assets/icons/stitch.svg')
            },
            {
              title: 'Colors',
              link: '/library/colors',
              icon: require('@/assets/icons/colours.svg')
            }
          ]
        },
        {
          name: 'Workspaces',
          items: [
            {
              title: 'My Watchlist',
              link: '/watchlist',
              icon: require('@/assets/icons/star.svg'),
              show: () => true
            },
            {
              title: 'My Renders',
              link: '/jobs',
              icon: require('@/assets/icons/render.svg'),
              show: () => true
            },
            {
              title: 'Styles',
              link: '/styles',
              icon: require('@/assets/icons/style.svg'),
              show: () => true
            }
          ]
        },
        {
          name: 'Settings',
          items: [
            {
              title: 'Admin Tools',
              link: '/admin-tools',
              icon: require('@/assets/icons/physics.svg'),
              show: () => canUserAccessUserManagement()
            },
            {
              title: 'Stitch Plugin',
              link: '/plugin',
              icon: require('@/assets/icons/plugin.svg'),
              show: FeatureFlags.canUserAccessStitchPlugin
            },
            {
              title: 'FAQs',
              link: '/faq',
              icon: require('@/assets/icons/faq.png'),
              show: () => true
            }
          ]
        }
      ]
    },

    /**
     * @returns {object}
     */
    visibleMenuItems () {
      const menuGroups = this.menuItems.filter(
        group => group.show === undefined || group.show()
      )

      return menuGroups.reduce(
        (accumulator, value) => ({
          ...accumulator,
          [value.name]: value.items.filter(
            item => item.show === undefined || item.show()
          )
        }),
        {}
      )
    },

    /**
     * @returns {object}
     */
    visibleMainMenuItems () {
      return Object.keys(this.visibleMenuItems).reduce((accumulator, name) => {
        if (name.toLowerCase() === 'settings') {
          return accumulator
        }

        return {
          ...accumulator,
          [name]: this.visibleMenuItems[name]
        }
      }, {})
    },

    /**
     * @returns {Array}
     */
    visibleSettingsItems () {
      return this.visibleMenuItems.Settings || []
    }
  },

  methods: {
    /**
     * Toggle the left menu between expanded and icon-only states.
     *
     * @returns {void}
     */
    toggleCollapse () {
      this.isCollapsed = !this.isCollapsed
    }
  }
}
</script>

<style lang="scss" scoped>
$menu-expanded-width: spacing(27); // 216px
$menu-collapsed-width: spacing(9.5); // 76px
$menu-motion: 160ms cubic-bezier(0.2, 0.8, 0.2, 1);

$menu-icon-size: spacing(2.5);
$menu-icon-collapsed-size: spacing(7/2);
$menu-item-height: spacing(6);
$menu-footer-height: spacing(7);
$menu-active-indicator-width: spacing(0.5);
$menu-active-background: #f1f1ef;
$menu-hover-background: #f7f7f5;
$menu-text-color: #242424;
$menu-icon-color: #555;
$menu-divider-color: #e8e8e5;
$menu-divider-width: 1px;
$menu-toggle-size: spacing(4);
$menu-toggle-edge-offset: spacing(2);
$menu-toggle-bottom-offset: spacing(1.75);
$menu-toggle-z-index: 2;
$menu-toggle-color: #71767c;
$menu-toggle-hover-color: #15171a;
$menu-toggle-hover-background: #f5f5f3;
$menu-toggle-focus-shadow: 0 0 0 2px rgba(21, 23, 26, 0.12);
$menu-toggle-radius: spacing(0.5);
$menu-toggle-icon-size: spacing(2);

.the-aside-left {
  position: relative;
  display: flex;
  flex: 0 0 $menu-expanded-width;
  flex-direction: column;
  box-sizing: border-box;
  width: $menu-expanded-width;
  height: percentage(100);
  min-height: 0;
  padding: 0;
  overflow: hidden;
  font-family: $primary-font;
  background-color: $white;
  transition: width $menu-motion, flex-basis $menu-motion;
}

.the-aside-left--collapsed {
  flex-basis: $menu-collapsed-width;
  width: $menu-collapsed-width;
}

.the-aside-left__nav {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  box-sizing: border-box;
  width: percentage(100);
  min-height: 0;
  padding: spacing(2) spacing(4.5) spacing(2) 0;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: $white;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  transition: padding $menu-motion;
}

.the-aside-left--collapsed .the-aside-left__nav {
  align-items: center;
  padding: spacing(2) 0;
}

.the-aside-left__nav-menu {
  display: flex;
  flex-direction: column;
  width: percentage(100);
  margin: 0;
}

.the-aside-left__nav-menu-title {
  display: none;
}

.the-aside-left__bottom {
  flex: 0 0 auto;
  width: percentage(100);
  margin-top: auto;
  background-color: $white;
}

.the-aside-left__bottom-menu {
  position: relative;
  display: flex;
  flex-direction: column;
  width: percentage(100);
  padding-top: spacing(2.25);

  &::before {
    position: absolute;
    top: spacing(0.75);
    right: spacing(1.5);
    left: spacing(2.5);
    height: $menu-divider-width;
    background-color: $menu-divider-color;
    content: '';
  }
}

.the-aside-left--collapsed .the-aside-left__bottom-menu {
  align-items: center;
}

.the-aside-left__footer {
  display: flex;
  flex: 0 0 $menu-footer-height;
  align-items: flex-end;
  justify-content: flex-end;
  box-sizing: border-box;
  width: percentage(100);
  min-height: $menu-footer-height;
  padding: 0 $menu-toggle-edge-offset $menu-toggle-bottom-offset 0;
  background-color: $white;
}

.the-aside-left--collapsed .the-aside-left__footer {
  justify-content: center;
  padding-right: 0;
  padding-left: 0;
}

.the-aside-left--collapsed .the-aside-left__nav-menu {
  align-items: center;
}

.the-aside-left__nav-menu-item-icon {
  display: block;
  flex: 0 0 $menu-icon-size;
  width: $menu-icon-size;
  height: $menu-icon-size;
  margin: 0;
  background-color: $menu-icon-color;
  transition:
    width $menu-motion,
    height $menu-motion,
    background-color $transition;
}

.the-aside-left--collapsed .the-aside-left__nav-menu-item-icon {
  flex: 0 0 auto;
  width: $menu-icon-collapsed-size;
  height: $menu-icon-collapsed-size;
  margin: 0;
}

.the-aside-left__nav-menu-item-icon--faq {
  background-color: $menu-icon-color;
}

.the-aside-left--collapsed .the-aside-left__nav-menu-item-icon--faq {
  width: spacing(3);
  height: spacing(3);
}

.the-aside-left__nav-menu-item {
  position: relative;
  display: flex;
  flex-shrink: 0;
  gap: spacing(1.5);
  align-items: center;
  box-sizing: border-box;
  min-width: 0;
  min-height: $menu-item-height;
  margin: spacing(0.25) 0;
  padding: 0 spacing(2) 0 spacing(3);
  overflow: hidden;
  color: $menu-text-color;
  font-weight: $font-weight-medium;
  font-size: spacing(1.6);
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  background-color: transparent;
  border: 0;
  border-radius: 0 spacing(1) spacing(1) 0;
  transition:
    color $transition,
    background-color $transition,
    padding $menu-motion;

  &::before {
    position: absolute;
    top: spacing(1);
    bottom: spacing(1);
    left: 0;
    width: $menu-active-indicator-width;
    background-color: transparent;
    border-radius: 0 spacing(0.5) spacing(0.5) 0;
    transition: background-color $transition;
    content: '';
  }

  &:hover {
    color: $black;
    background-color: $menu-hover-background;

    .the-aside-left__nav-menu-item-icon {
      background-color: $black;
    }
  }

  &:last-of-type {
    margin-bottom: spacing(0.25);
    border-bottom: 0;
  }
}

.the-aside-left--collapsed .the-aside-left__nav-menu-item {
  gap: 0;
  justify-content: center;
  width: percentage(100);
  padding: 0;
  font-size: 0;
}

.the-aside-left__nav-menu-item-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 1;
  transition: opacity $menu-motion;
}

.the-aside-left--collapsed .the-aside-left__nav-menu-item-label {
  width: 0;
  opacity: 0;
}

.the-aside-left__nav-menu-item-arrow-icon {
  position: absolute;
  left: spacing(6.5);
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  margin: 0;
  font-size: spacing(1.5);
  line-height: 1;
  transition: opacity $menu-motion;
}

.the-aside-left--collapsed .the-aside-left__nav-menu-item-arrow-icon {
  opacity: 0;
  pointer-events: none;
}

.the-aside-left__nav-menu-item.router-link-active,
.the-aside-left__nav-menu-item.router-link-exact-active {
  color: $black;
  font-weight: $font-weight-semibold;
  background-color: $menu-active-background;

  &::before {
    background-color: $black;
  }

  .the-aside-left__nav-menu-item-icon {
    background-color: $black;
  }
}

.the-aside-left .the-aside-left__nav-menu-item--settings {
  border: 0;
}

.the-aside-left__toggle {
  position: static;
  z-index: $menu-toggle-z-index;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: $menu-toggle-size;
  height: $menu-toggle-size;
  margin: 0;
  padding: 0;
  color: $menu-toggle-color;
  background-color: transparent;
  border: 0;
  border-radius: $menu-toggle-radius;
  outline: 0;
  box-shadow: none;
  cursor: pointer;
  transition:
    color $transition,
    background-color $transition,
    box-shadow $transition;

  &:hover {
    color: $menu-toggle-hover-color;
    background-color: $menu-toggle-hover-background;
  }

  &:focus-visible {
    color: $menu-toggle-hover-color;
    box-shadow: $menu-toggle-focus-shadow;
  }
}

.the-aside-left__toggle-icon {
  font-size: $menu-toggle-icon-size;
  line-height: 1;
}
</style>
